import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sofa,
  Palette,
  Hammer,
  Lightbulb,
  Sparkles,
  Compass,
  ArrowRight,
  Check,
  MapPin,
  Phone,
  Clock,
  Award,
  MessageCircle,
} from "lucide-react";
import api from "@/lib/api";
import ConsentCheckbox from "@/components/ConsentCheckbox";
import { buildWhatsAppLink } from "@/components/WhatsAppFab";

const SERVICES = [
  { icon: Compass, title: "Space Planning", body: "Optimised floor plans, traffic flow and zoning that turn 1,200 sqft into a 1,500-sqft feel." },
  { icon: Palette, title: "Concept & Mood Boards", body: "From quiet luxe to maximalist warmth — three curated directions before a single tile is laid." },
  { icon: Sofa, title: "Bespoke Furniture", body: "Custom-built sofas, beds, vanities and wardrobes from in-house karkhana and trusted ateliers." },
  { icon: Hammer, title: "Civil &amp; Carpentry", body: "False ceilings, flooring, painting, modular kitchens, wardrobes — turnkey or à la carte." },
  { icon: Lightbulb, title: "Lighting &amp; Smart Home", body: "Layered lighting design, automated scenes, voice control via Apple Home / Alexa." },
  { icon: Sparkles, title: "Soft Furnishing &amp; Décor", body: "Drapes, rugs, art curation, accessories — the final 10% that does 50% of the work." },
];

const PACKAGES = [
  {
    name: "Essentials",
    pitch: "Move-in ready",
    price: "₹2.5 L+",
    perks: [
      "Modular kitchen + 2 wardrobes",
      "False ceiling in living + lighting",
      "Wall painting (premium emulsion)",
      "Curtains &amp; basic décor",
      "12-month carpentry warranty",
    ],
  },
  {
    name: "Signature",
    pitch: "Most popular",
    price: "₹6 L+",
    perks: [
      "Full kitchen + all wardrobes (laminates)",
      "Designer false ceiling + cove lighting",
      "Premium paint + 1 accent wallpaper",
      "Bespoke study / vanity / bar",
      "Smart switches + automation basics",
      "Curated art &amp; soft furnishing",
      "24-month warranty",
    ],
    featured: true,
  },
  {
    name: "Bespoke",
    pitch: "Ultra-luxury",
    price: "₹15 L+",
    perks: [
      "Veneer / acrylic finishes throughout",
      "Imported flooring (Italian marble / wood)",
      "Custom upholstered furniture",
      "Full home automation",
      "Walk-in wardrobe + cinema room",
      "Art consultation &amp; curation",
      "60-month warranty + annual servicing",
    ],
  },
];

const SERVICEABLE_CITIES = [
  { name: "Lucknow", note: "In-house team · 24-hr response" },
  { name: "Gurugram", note: "Partner studio · NCR hub" },
  { name: "Noida", note: "Partner studio · Sector 18" },
  { name: "Delhi", note: "On-call site supervisor" },
  { name: "Pune", note: "Partner studio · Koregaon Park" },
];

const STEPS = [
  { n: "01", t: "Site Visit &amp; Brief", d: "We measure, photograph and listen — what works for your routine, family and budget." },
  { n: "02", t: "Concept Design", d: "3 mood boards, 3D walkthroughs and a complete BoQ within 14 days." },
  { n: "03", t: "Production", d: "Factory-finished joinery, on-site civil and electrical — daily progress photo updates." },
  { n: "04", t: "Handover", d: "Snag-free handover, styling day, and a photo-shoot of your new home." },
];

export default function InteriorWorks() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "I'd like a quote for interior design." });
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please share name, email and phone.");
      return;
    }
    if (!consent) {
      toast.error("Please tick the consent box to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiry", { ...form, message: `[Interior Works] ${form.message}`, consent: true });
      toast.success("Thank you. Our design studio will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setConsent(false);
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="interior-works-page" className="pb-32">
      {/* Hero */}
      <section className="relative h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=2400"
            alt="Luxury interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12 pb-20">
          <div className="overline mb-5 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Interior Works · Available in Select Cities</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-slate-900 font-light leading-[1.05] max-w-4xl">
            Buy the property. <br />
            <em className="text-[#D4A437] not-italic">Move into a home.</em>
          </h1>
          <p className="text-slate-700 text-lg mt-8 max-w-2xl font-light leading-relaxed">
            Turnkey interior design and execution by our in-house studio in Lucknow and partner ateliers
            across Gurugram, Noida, Delhi and Pune. From concept to handover — all under one roof.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#iw-contact"
              data-testid="iw-cta-talk"
              className="inline-flex items-center gap-2 bg-[#D4A437] hover:bg-[#D4A437] text-white font-medium tracking-wide px-7 py-3.5 transition-colors"
            >
              Book a Site Visit <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#iw-packages"
              data-testid="iw-cta-packages"
              className="inline-flex items-center gap-2 border border-slate-200 hover:border-[#D4A437] hover:text-[#D4A437] text-slate-800 px-7 py-3.5 transition-colors"
            >
              View Packages
            </a>
          </div>
        </div>
      </section>

      {/* Promise strip */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-100 border border-slate-100">
        {[
          { icon: Clock, k: "45 days", v: "Avg Turnaround" },
          { icon: Award, k: "60-month", v: "Carpentry Warranty" },
          { icon: Sparkles, k: "100%", v: "Factory-finished" },
          { icon: Check, k: "Fixed", v: "Price Quotes" },
        ].map((s) => (
          <div key={s.v} className="bg-white p-8 text-center" data-testid={`iw-stat-${s.v.replace(/\s+/g, "-").toLowerCase()}`}>
            <s.icon className="w-5 h-5 text-[#D4A437] mx-auto mb-3" strokeWidth={1.5} />
            <div className="font-serif text-2xl text-slate-900">{s.k}</div>
            <div className="overline mt-2">{s.v}</div>
          </div>
        ))}
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="overline mb-4">What we cover</div>
        <h2 className="font-serif text-4xl sm:text-5xl font-light text-slate-900 mb-12 max-w-3xl">
          End-to-end interior services.
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-100 border border-slate-100">
          {SERVICES.map((s) => (
            <div key={s.title} className="bg-white p-8 group" data-testid={`iw-service-${s.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <s.icon className="w-7 h-7 text-[#D4A437] mb-5 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <h3 className="font-serif text-lg text-slate-900 mb-3" dangerouslySetInnerHTML={{ __html: s.title }} />
              <p className="text-sm text-slate-600 leading-relaxed font-light" dangerouslySetInnerHTML={{ __html: s.body }} />
            </div>
          ))}
        </div>
      </section>

      {/* Packages */}
      <section id="iw-packages" className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="overline mb-4">Transparent pricing</div>
        <h2 className="font-serif text-4xl sm:text-5xl font-light text-slate-900 mb-2 max-w-3xl">Three signature packages.</h2>
        <p className="text-slate-600 mb-12 max-w-2xl font-light">Indicative pricing for a 2BHK apartment (≈1,000 sqft). Final quote depends on size, finish and inventory.</p>
        <div className="grid md:grid-cols-3 gap-px bg-slate-100 border border-slate-100">
          {PACKAGES.map((p) => (
            <div
              key={p.name}
              data-testid={`iw-package-${p.name.toLowerCase()}`}
              className={`p-8 ${p.featured ? "bg-[#D4A437]/5 border-2 border-amber-200 -m-px relative z-10" : "bg-white"}`}
            >
              {p.featured && (
                <div className="overline text-[#D4A437] mb-4 inline-block bg-[#D4A437]/10 px-3 py-1">{p.pitch}</div>
              )}
              {!p.featured && <div className="overline mb-4">{p.pitch}</div>}
              <div className="font-serif text-3xl text-slate-900">{p.name}</div>
              <div className="font-serif text-4xl text-[#D4A437] mt-4">{p.price}</div>
              <div className="text-xs text-slate-500 mt-1">starting · for ~1,000 sqft</div>
              <ul className="mt-8 space-y-3">
                {p.perks.map((perk, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-[#D4A437] mt-0.5 shrink-0" strokeWidth={2} />
                    <span dangerouslySetInnerHTML={{ __html: perk }} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Serviceable cities */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32 grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-5">
          <div className="overline mb-4 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Service Cities</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 font-light leading-tight">Available in select cities — by design.</h2>
          <p className="text-slate-600 mt-6 font-light leading-relaxed">
            We don&apos;t franchise. Every project is delivered by our own studio in Lucknow or a vetted partner atelier
            in NCR &amp; Pune — so quality and timelines stay non-negotiable.
          </p>
        </div>
        <div className="md:col-span-7 grid sm:grid-cols-2 gap-px bg-slate-100 border border-slate-100">
          {SERVICEABLE_CITIES.map((c) => (
            <div key={c.name} className="bg-white p-6" data-testid={`iw-city-${c.name.toLowerCase()}`}>
              <div className="font-serif text-xl text-slate-900">{c.name}</div>
              <div className="text-xs text-slate-600 mt-2 font-light">{c.note}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-32">
        <div className="overline mb-4">Process</div>
        <h2 className="font-serif text-4xl sm:text-5xl font-light text-slate-900 mb-12 max-w-3xl">From brief to handover — typically 45 days.</h2>
        <div className="grid md:grid-cols-4 gap-px bg-slate-100 border border-slate-100">
          {STEPS.map((s) => (
            <div key={s.n} className="bg-white p-8" data-testid={`iw-step-${s.n}`}>
              <div className="overline mb-4">{s.n}</div>
              <h3 className="font-serif text-lg text-slate-900 mb-3" dangerouslySetInnerHTML={{ __html: s.t }} />
              <p className="text-sm text-slate-600 leading-relaxed font-light">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Lead capture */}
      <section
        id="iw-contact"
        className="max-w-7xl mx-auto px-6 md:px-12 mt-32 bg-amber-50/40 border border-slate-200 p-10 md:p-16 grid md:grid-cols-12 gap-12"
      >
        <div className="md:col-span-5">
          <div className="overline mb-4">Design Studio</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 font-light leading-tight">Book a free site visit.</h2>
          <p className="text-slate-600 mt-6 font-light leading-relaxed">
            One of our senior designers will visit your home, take detailed measurements,
            and revert with a concept board + indicative quote within 7 days.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-slate-700">
            <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#D4A437]" /> +91 522 4000 800</li>
            <li className="flex items-center gap-3"><Sofa className="w-4 h-4 text-[#D4A437]" /> interiors@ocinfraventures.com</li>
            <li className="flex items-start gap-3"><Check className="w-4 h-4 text-[#D4A437] mt-0.5" /> Free site visit · No obligation quote</li>
          </ul>
          <a
            href={buildWhatsAppLink("Hi OC Infraventures, I'd like a quote for interior design. Please share your packages and book a site visit.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="iw-whatsapp-btn"
            className="mt-8 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium px-6 py-3 transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
          </a>
        </div>
        <form onSubmit={submit} className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="iw-contact-form">
          <div><label className="overline block mb-2">Full Name</label>
            <input data-testid="iw-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
              className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div><label className="overline block mb-2">Email</label>
            <input type="email" data-testid="iw-input-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
              className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div className="sm:col-span-2"><label className="overline block mb-2">Phone</label>
            <input data-testid="iw-input-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required
              className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div className="sm:col-span-2"><label className="overline block mb-2">Tell us about the space (city, area, BHK)</label>
            <textarea data-testid="iw-input-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4}
              className="w-full bg-white border border-slate-200 text-slate-900 px-3 py-3 text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div className="sm:col-span-2">
            <ConsentCheckbox checked={consent} onChange={setConsent} testId="inq-consent-interior" />
            <button type="submit" disabled={submitting || !consent} data-testid="iw-submit-btn"
              className="w-full sm:w-auto mt-5 bg-[#D4A437] hover:bg-[#D4A437] text-white font-medium tracking-wide px-8 py-3.5 transition-colors disabled:opacity-50">
              {submitting ? "Sending..." : "Book Free Site Visit →"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
